<template>
  <div>
    <div class="bg-white shadow rounded-lg divide-y divide-gray-200 overflow-hidden">
      <div class="px-4 py-3 sm:px-6 py-4 rounded-t-lg">
        {{ facebookPage.name }}
      </div>
      <div class="px-4 py-5 sm:p-6 bg-gray-100">
        <FacebookPageInsightsQueryBuilder @query="fetchData" />
      </div>
      <div v-if="loading" class="p-12 text-center">
        <Loader />
      </div>
      <div v-else class="flex">
        <div class="w-64 space-y-2 bg-gray-100 border-r border-gray-200">
          <ul class="divide-y divide-gray-200">
            <li
              v-for="metric in metrics"
              :key="metric.name"
              :class="[currentMetricName === metric.name ? 'bg-white -mr-px' : '']"
            >
              <label
                class="px-4 py-3 text-sm flex flex-1 space-x-2 items-center"
                v-tip="{ content: metric.description }"
              >
                <input
                  v-model="currentMetricName"
                  type="radio"
                  :value="metric.name"
                  class="hidden"
                />
                <span class="flex-1 truncate">
                  {{ metric.title }}
                </span>
              </label>
            </li>
          </ul>
        </div>
        <div class="flex-1">
          <div v-if="currentMetric" class="divide-y divide-gray-200">
            <div class="px-4 py-3 sm:px-6 sm:py-4">
              <h3 class="text-lg font-medium">
                {{ metricByName[currentMetricName].title }}
              </h3>
              <p class="text-gray-600 text-sm">
                {{ metricByName[currentMetricName].description }}
              </p>
            </div>
            <div class="px-4 py-3 sm:px-6 sm:py-4">
              <template v-if="currentMetric.singleValues">
                <AreaChart
                  :key="`area_${currentMetricName}`"
                  :values="currentMetric.values"
                  :metric="metricByName[currentMetricName]"
                />
                <h4 class="text-base font-medium text-center mt-10 mb-4">
                  Facebook Campaigns Active By Day
                </h4>
                <TimelineChart
                  :key="`timeline_${currentMetricName}`"
                  :data="timelineData"
                  :range="getFacebookPageInsightsResultDateRange()"
                />
              </template>
              <InsightsTable
                v-else
                :key="`table_${currentMetricName}`"
                :values="currentMetric.values"
                :metric="metricByName[currentMetricName]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import { accountReportsClient, accountFacebookPageInsightsClient } from '@/api/clients';
import convertReportDataToTimelineChartData from '@/utils/convert-report-data-to-timeline-chart-data';
import AreaChart from './facebook-page-insights/area-chart.vue';
import TimelineChart from './facebook-page-insights/timeline-chart.vue';
import InsightsTable from './facebook-page-insights/table.vue';
import FacebookPageInsightsQueryBuilder from './facebook-page-insights-query-builder.vue';

export default {
  components: {
    AreaChart,
    TimelineChart,
    InsightsTable,
    FacebookPageInsightsQueryBuilder,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    facebookPageId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      rejectReason: '',
      facebookPageInsights: null,
      facebookCampaignInsights: null,
      currentMetricName: null,
    };
  },
  computed: {
    facebookPage() {
      return this.$store.state.entities.facebookPages.byId[this.facebookPageId];
    },
    metrics() {
      if (!Array.isArray(this.facebookPageInsights)) {
        return [];
      }

      const validResults = this.facebookPageInsights.filter(
        (result) => typeof result.name === 'string'
      );

      const hueStep = Number.parseInt(360 / validResults.length, 10);

      return validResults.map((result, index) => ({
        name: result.name,
        title: result.title,
        description: result.description,
        color: `hsl(${hueStep * index}, 100%, 50%, 1)`,
        backgroundColor: `hsl(${hueStep * index}, 100%, 50%, .10)`,
      }));
    },
    metricByName() {
      return this.metrics.reduce(
        (metricByName, metric) => ({
          ...metricByName,
          [metric.name]: {
            ...metric,
          },
        }),
        {}
      );
    },
    valuesByMetric() {
      if (!Array.isArray(this.facebookPageInsights)) {
        return [];
      }

      return this.facebookPageInsights
        .filter((result) => typeof result.name === 'string')
        .reduce((valuesByMetric, result) => {
          const clonedResult = cloneDeep(result);
          return {
            ...valuesByMetric,
            [result.name]: {
              singleValues: typeof clonedResult.values[0].value === 'number',
              values: clonedResult.values.map((resultValue) => {
                const date = moment(resultValue.end_time).subtract(1, 'days').format('YYYY-MM-DD');

                return {
                  date,
                  value: resultValue.value,
                };
              }),
            },
          };
        }, {});
    },
    currentMetric() {
      if (!this.currentMetricName || !(this.currentMetricName in this.valuesByMetric)) {
        return null;
      }

      return this.valuesByMetric[this.currentMetricName];
    },
    timelineData() {
      return convertReportDataToTimelineChartData(this.facebookCampaignInsights);
    },
  },
  created() {
    window.Apex = {
      chart: {
        height: 160,
      },
      dataLabels: {
        enabled: false,
      },
    };
  },
  methods: {
    setLoading(loading = true) {
      this.loading = Boolean(loading);
    },
    async fetchData(query) {
      this.facebookPageInsights = null;
      this.facebookCampaignInsights = null;
      try {
        this.setLoading(true);
        this.facebookPageInsights = await this.fetchFacebookPageInsights(query);
        this.facebookCampaignInsights = await this.fetchFacebookCampaignInsights();

        this.$nextTick(() => {
          this.currentMetricName = this.metrics?.[0]?.name || null;
        });
      } catch (error) {
        this.rejectReason = error?.response?.message || 'Something went wrong!';
      } finally {
        this.setLoading(false);
      }
    },
    async fetchFacebookPageInsights(query) {
      try {
        const { data } = await accountFacebookPageInsightsClient.read(
          this.accountId,
          this.facebookPageId,
          query
        );
        return Promise.resolve(data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchFacebookCampaignInsights() {
      try {
        const [since, until] = this.getFacebookPageInsightsResultDateRange();
        const { data } = await accountReportsClient.facebookCampaignActivePeriods(this.accountId, {
          filter: {
            since,
            until,
          },
        });
        return Promise.resolve(data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getFacebookPageInsightsResultDateRange() {
      if (!Array.isArray(this.facebookPageInsights) || this.facebookPageInsights.length === 0) {
        return [];
      }

      const [sample] = this.facebookPageInsights;

      if (!Array.isArray(sample?.values) || sample.values.length === 0) {
        return [];
      }

      const dates = sample.values
        .filter(
          (value) => typeof value?.end_time === 'string' // has date
        )
        .map((value) => moment(value.end_time).subtract(1, 'days').format('YYYY-MM-DD'));

      if (dates.length === 0) {
        return [];
      }

      if (dates.length === 1) {
        return [dates[0], dates[0]];
      }

      return [dates.shift(), dates.pop()];
    },
  },
};
</script>
